import { removeNamespace } from '@/utils/helpers'
import { GiftCardType, GiftCardTypeTab, GiftCardStateTab, GiftCardSortBy, GiftCardSearchType } from '../../utils/enums/giftcard-enums'
import GiftCardService from '@/services/giftcard.service'
import * as cloneDeep from 'lodash.clonedeep'

export const GIFTCARDS_TYPES = {
  getters: {
    typeTabs: 'giftcards/typeTabs',
    stateTabs: 'giftcards/stateTabs',
    currentTab: 'giftcards/currentTab'
  },
  mutations: {
    SET_LOADING: 'giftcards/SET_LOADING',
    SET_TYPE_TAB: 'giftcards/SET_TYPE_TAB',
    SET_STATE_TAB: 'giftcards/SET_STATE_TAB',
    SET_PAGED_GIFTCARDS: 'giftcards/SET_PAGED_GIFTCARDS',
    SET_TAB_GIFTCARD_TOTAL: 'giftcards/SET_TAB_GIFTCARD_TOTAL',
    SET_TABLE_OPTIONS: 'giftcards/SET_TABLE_OPTIONS',
    SET_SEARCH_OPTIONS: 'giftcards/SET_SEARCH_OPTIONS',
    SET_SEARCH_TYPE: 'giftcards/SET_SEARCH_TYPE'
  },
  actions: {
    changeTab: 'giftcards/changeTab',
    changeTypeTab: 'giftcards/changeTypeTab',
    changeStateTab: 'giftcards/changeStateTab',
    changeSearch: 'giftcards/changeSearch',
    changeSort: 'giftcards/changeSort',
    changeTableOptions: 'giftcards/changeTableOptions',
    fetchGiftCardsPaged: 'giftcards/fetchGiftCardsPaged',
    startGiftCardReportFile: 'giftcards/startGiftCardReportFile',
    getDownloadStatus: 'giftcards/getDownloadStatus',
    changeSearchType: 'giftcards/changeSearchType'
  }
}

const _types = removeNamespace('giftcards', GIFTCARDS_TYPES)

const defaultTableOptions = {
  page: 1,
  itemsPerPage: 10
}

const defaultSearchOptions = {
  keyword: '',
  orderBy: GiftCardSortBy.CreatedAt,
  searchType: GiftCardSearchType.RecipientEmail,
  orderDescending: true
}

const tabs = [
  {
    type: GiftCardTypeTab.ecomGiftCard,
    state: GiftCardStateTab.All,
    params: {
      giftCardType: GiftCardType.ecomGiftCard,
      giftCardState: GiftCardStateTab.All
    }
  },
  {
    type: GiftCardTypeTab.ecomGiftCard,
    state: GiftCardStateTab.Active,
    params: {
      giftCardType: GiftCardType.ecomGiftCard,
      giftCardState: GiftCardStateTab.Active
    }
  },
  {
    type: GiftCardTypeTab.ecomGiftCard,
    state: GiftCardStateTab.Inactive,
    params: {
      giftCardType: GiftCardType.ecomGiftCard,
      giftCardState: GiftCardStateTab.Inactive
    }
  }
]

const state = () => ({
  loading: false,
  tabs: tabs,
  typeTab: GiftCardTypeTab.ecomGiftCard,
  stateTab: GiftCardStateTab.All,
  tableOptions: cloneDeep(defaultTableOptions),
  searchOptions: cloneDeep(defaultSearchOptions),
  pagedGiftCards: [],
  tabGiftCardTotal: 0
})

const getters = {
  [_types.getters.typeTabs]: state => {
    const tabs = state.tabs.map(x => x.type)
    return [...new Set(tabs)]
  },
  [_types.getters.stateTabs]: state => {
    return state.tabs.filter(x => x.type === state.typeTab)
      .map(x => x.state)
  },
  [_types.getters.currentTab]: state => {
    return state.tabs.find(x => x.type === state.typeTab && x.state === state.stateTab)
  }
}

const mutations = {
  [_types.mutations.SET_LOADING] (state, bool) {
    state.loading = bool
  },
  [_types.mutations.SET_TYPE_TAB] (state, typeTab) {
    state.typeTab = typeTab
  },
  [_types.mutations.SET_STATE_TAB] (state, stateTab) {
    state.stateTab = stateTab
  },
  [_types.mutations.SET_TABLE_OPTIONS] (state, options) {
    state.tableOptions = cloneDeep(options)
  },
  [_types.mutations.SET_SEARCH_OPTIONS] (state, options) {
    state.searchOptions = cloneDeep(options)
  },
  [_types.mutations.SET_PAGED_GIFTCARDS] (state, pagedResult) {
    state.pagedGiftCards = pagedResult
  },
  [_types.mutations.SET_TAB_GIFTCARD_TOTAL] (state, count) {
    state.tabGiftCardTotal = count
  },
  [_types.mutations.SET_SEARCH_TYPE] (state, type) {
    state.searchOptions.searchType = type
  }
}

const actions = {
  [_types.actions.changeTab] ({ state, commit, dispatch }, { typeTab, stateTab, dates, reasonCreated, tenant }) {
    commit(_types.mutations.SET_LOADING, true)
    commit(_types.mutations.SET_TYPE_TAB, typeTab)
    commit(_types.mutations.SET_STATE_TAB, stateTab)
    commit(_types.mutations.SET_PAGED_GIFTCARDS, [])
    commit(_types.mutations.SET_TAB_GIFTCARD_TOTAL, 0)
    commit(_types.mutations.SET_TABLE_OPTIONS, defaultTableOptions)
    commit(_types.mutations.SET_SEARCH_OPTIONS, defaultSearchOptions)
    dispatch(_types.actions.fetchGiftCardsPaged, { dates, reasonCreated, tenant })
  },
  [_types.actions.changeTypeTab] ({ state, dispatch }, { typeTab, dates, reasonCreated, tenant }) {
    dispatch(_types.actions.changeTab, {
      typeTab: typeTab,
      stateTab: state.stateTab,
      dates: dates,
      reasonCreated: reasonCreated,
      tenant: tenant
    })
  },
  [_types.actions.changeStateTab] ({ state, dispatch }, { stateTab, dates, reasonCreated, tenant }) {
    dispatch(_types.actions.changeTab, {
      typeTab: state.typeTab,
      stateTab,
      dates: dates,
      reasonCreated: reasonCreated,
      tenant: tenant
    })
  },
  [_types.actions.fetchGiftCardsPaged] ({ state, commit, getters }, { dates, reasonCreated, tenant }) {
    commit(_types.mutations.SET_LOADING, true)
    const { itemsPerPage, page } = state.tableOptions
    const { keyword, orderBy, orderDescending, searchType } = state.searchOptions
    const params = {
      ...getters.currentTab.params,
      keyword,
      searchType,
      skipCount: (page - 1) * itemsPerPage,
      maxResultCount: itemsPerPage,
      orderBy,
      orderDescending,
      startDate: dates[0],
      endDate: dates[1],
      tenant: tenant,
      reasonCreated: reasonCreated
    }
    return GiftCardService.getAllPaged(params)
      .then((res) => {
        const { items, totalCount } = res
        commit(_types.mutations.SET_PAGED_GIFTCARDS, items)
        commit(_types.mutations.SET_TAB_GIFTCARD_TOTAL, totalCount)
      })
      .finally(() => commit(_types.mutations.SET_LOADING, false))
  },
  [_types.actions.startGiftCardReportFile] ({ state, commit, getters }, { dates, reasonCreated, tenant }) {
    commit(_types.mutations.SET_LOADING, true)
    const { itemsPerPage, page } = state.tableOptions
    const { keyword, orderBy, orderDescending, searchType } = state.searchOptions
    const params = {
      ...getters.currentTab.params,
      keyword,
      searchType,
      skipCount: (page - 1) * itemsPerPage,
      maxResultCount: itemsPerPage,
      orderBy,
      orderDescending,
      startDate: dates[0],
      endDate: dates[1],
      tenant: tenant,
      reasonCreated: reasonCreated
    }
    return GiftCardService.startGiftCardReportFile(params).finally(() => commit(_types.mutations.SET_LOADING, false))
  },
  [_types.actions.getDownloadStatus] ({ state, commit, getters }, taskId) {
    commit(_types.mutations.SET_LOADING, true)
    return GiftCardService.getDownloadStatus(taskId).finally(() => commit(_types.mutations.SET_LOADING, false))
  },
  [_types.actions.changeSearchType] ({ state, commit, dispatch }, searchType) {
    commit(_types.mutations.SET_SEARCH_TYPE, searchType)
  },
  [_types.actions.changeSearch] ({ state, commit, dispatch }, { searchTerm, dates, reasonCreated, tenant }) {
    const keyword = searchTerm ? searchTerm.replace(/(<([^>]+)>)/ig, '') : ''
    commit(_types.mutations.SET_SEARCH_OPTIONS, {
      ...state.searchOptions,
      keyword
    })
    dispatch(_types.actions.fetchGiftCardsPaged, { dates: dates, reasonCreated: reasonCreated, tenant: tenant })
  },
  [_types.actions.changeSort] ({ state, commit, dispatch }, { orderBy, orderDescending, dates, reasonCreated, tenant }) {
    commit(_types.mutations.SET_SEARCH_OPTIONS, {
      ...state.searchOptions,
      orderBy,
      orderDescending
    })
    dispatch(_types.actions.fetchGiftCardsPaged, { dates: dates, reasonCreated: reasonCreated, tenant: tenant })
  },
  [_types.actions.changeTableOptions] ({ state, commit, dispatch }, { page, itemsPerPage, dates, reasonCreated, tenant }) {
    if (page !== state.tableOptions.page || itemsPerPage !== state.tableOptions.itemsPerPage) {
      commit(_types.mutations.SET_TABLE_OPTIONS, {
        ...state.tableOptions,
        page,
        itemsPerPage
      })
      dispatch(_types.actions.fetchGiftCardsPaged, { dates: dates, reasonCreated: reasonCreated, tenant: tenant })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
